/**
 * Built-in defaults for the editable CFP page content block (`cfp`).
 *
 * These mirror the content that has always been hardcoded on the Call for
 * Papers page, so the public page renders identically until an admin saves a
 * customisation. Shared by the public page (`pages/Cfp.jsx`,
 * `components/content/PublicationIndexingSection.jsx`) and the admin editor
 * (`admin/pages/PageCfp.jsx`).
 */

export const CFP_IMPORTANT_DATES = [
  { label: 'Paper Due', date: '8th July 2026', status: 'open' },
  { label: 'Notification', date: '10th August 2026', status: 'tba' },
  { label: 'Final Manuscript Of Submission Due', date: '20th August 2026', status: 'tba' },
  { label: 'Registration Due', date: '20th August 2026', status: 'tba' },
  { label: 'Conference Dates', date: '17–19 September 2026', status: 'tba' },
];

export const CFP_GUIDELINES = {
  eyebrow: 'Submission Guidelines',
  title: 'Format your paper, then submit via EasyChair.',
  paragraphs: [
    'Authors are invited to submit formatted full papers including results, tables, figures, and references. Submissions must be original work, not currently under review at any other venue.',
    'All papers undergo a rigorous double-blind peer-review process. Accepted papers will appear in the Springer proceedings and must be presented at the conference.',
  ],
  primaryLabel: 'Submit on EasyChair',
  primaryUrl: 'https://easychair.org/conferences/?conf=isbm2026',
  secondaryLabel: 'Author Guidelines',
  secondaryUrl: '/author-guidelines',
  logo: 'assets/images/logo/logoEC.png',
  logoAlt: 'EasyChair — The world for scientists',
};

export const CFP_PUBLICATION = {
  eyebrow: 'Published by Springer Nature, indexed worldwide',
  title: 'Publication',
  body:
    'Accepted papers from ISBM 2026 will appear in the Springer Nature proceedings by Springer LNNS, Series : <href="https://link.springer.com/series/15179">https://link.springer.com/series/15179</href> ' +
    'Proceedings are indexed by SCOPUS, Web of Science, EI Compendex, INSPEC, DBLP, SCImago, zbMATH, and Google Scholar.',
  proceedingsLinkLabel: 'See past proceedings',
  logos: [
    { name: 'Springer', src: 'assets/images/logo/springer.png' },
    { name: 'Springer Nature', src: 'assets/images/logo/springer_nature.png' },
  ],
};

export const CFP_INDEXING = {
  eyebrow: 'Indexing',
  title: 'Indexing',
  body:
    'Indexed by SCOPUS, EI Compendex, INSPEC, WTI Frankfurt eG, zbMATH, Japanese Science and Technology Agency (JST), SCImago, DBLP. ' +
    'All books published in the series are submitted for consideration in the Web of Science.',
  logos: [
    { name: 'Scopus', src: 'assets/images/lnns/SCOPUS.png' },
    { name: 'EI Compendex', src: 'assets/images/lnns/EI-Compendex.png' },
    { name: 'IET Inspec', src: 'assets/images/lnns/INSPEC.png' },
    { name: 'WTI Frankfurt eG', src: 'assets/images/lnns/WTI-Frankfurt-eG.png' },
    { name: 'zbMATH Open', src: 'assets/images/lnns/zbMATH.png' },
    { name: 'Japan Science and Technology Agency (JST)', src: 'assets/images/sist/Japanese-Science-and-Technology-Agency-(JST).png' },
    { name: 'SCImago', src: 'assets/images/lnns/SCImago.png' },
    { name: 'DBLP', src: 'assets/images/sist/DBLP.png' },
  ],
};

export const CFP_RESOURCES = {
  eyebrow: 'Resources',
  title: 'Important Downloads.',
  lead: 'Templates, guidelines, and author resources for paper preparation and submission.',
  groups: [
    {
      group: 'Guidelines',
      items: [
        { title: 'Proceedings Guidelines for Authors', description: 'Manuscript preparation standards', url: '/download/Proceedings-guidelines-for-authors-Springer.pdf', external: false },
        { title: 'Author Guideline for Paper Submission', description: 'Step-by-step submission walkthrough', url: '/author-guidelines', external: false },
        { title: 'Paper Submission Guidelines', description: 'Paper submission guidelines', url: '/author-guidelines', external: false },
      ],
    },
    {
      group: 'Manuscript Templates',
      items: [
        { title: 'Sample LaTeX Paper', description: 'Reference template to build upon', url: '/download/Sample-Latex-paper.pdf', external: false },
        { title: 'LaTeX Instructions for Authors', description: 'Compilation & macro guide', url: '/download/Latex-instructions-for-authors.pdf', external: false },
        { title: 'LaTeX Package', description: 'Full style files & class packages', url: '/download/Latex-Package.zip', external: false },
        { title: 'Word Templates', description: 'Microsoft Word manuscript template', url: '/download/Word-Templates.zip', external: false },
      ],
    },
    {
      group: 'Policies',
      items: [
        { title: 'Plagiarism Policy', description: 'Authors must adhere to the Springer Policy and Procedures on Plagiarism before submitting', url: 'http://www.springer.com/authors/book+authors/helpdesk?SGWID=0-1723113-12-807204-0', external: true },
      ],
    },
  ],
};

export const CFP_DEFAULTS = {
  importantDates: CFP_IMPORTANT_DATES,
  guidelines: CFP_GUIDELINES,
  publication: CFP_PUBLICATION,
  indexing: CFP_INDEXING,
  // Optional extra "Publication & Indexing" / "Indexing" sections an admin can
  // add from the CFP editor. Each entry has the same shape as the primary
  // section above (eyebrow, title, body, logos[]). Empty by default.
  additionalPublications: [],
  additionalIndexing: [],
  resources: CFP_RESOURCES,
};
